import { useEffect } from 'react'
import { styled } from '@mui/material/styles'
import {
  AlertCircleOutlinedIcon,
  CancelOutlinedIcon,
  CheckCircleIcon,
} from '@weblite/icons'
import { Typography } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { toLocale } from '$func/text'
import { getOutDatedBrowserMeta } from '$setup/error-assistant'
import { CompatibilityWarningLayout } from '$common/compatibility-warning-layout'
import { t } from '$i18n'

const browserLogoImages: Record<string, string> = {
  Safari: 'safari-logo',
  'Mobile Safari': 'safari-logo',

  Chrome: 'chrome-logo',
  Chromium: 'chrome-logo',

  'Samsung Browser': 'samsung-internet-logo',

  Firefox: 'firefox-logo',

  other: 'browser',
}

export function OutDatedBrowser({ isOpen }: { isOpen: boolean }) {
  const { clientBrowser, clientBrowserVersion, recommendedBrowserVersion } =
    getOutDatedBrowserMeta() || {}

  useEffect(() => {
    if (!clientBrowser || !clientBrowserVersion || !recommendedBrowserVersion)
      closeCurrentModal()
  }, [])

  const browserLogo =
    clientBrowser && browserLogoImages[clientBrowser]
      ? browserLogoImages[clientBrowser]
      : browserLogoImages.other

  return (
    <CompatibilityWarningLayout
      isOpen={isOpen}
      icon={
        <BrowserLogo
          id="browser-logo"
          style={{ width: '64px' }}
          src={`assets/icons/${browserLogo}.svg`}
        />
      }
      title={t('warningPages.outdatedBrowser.title')}
      description={
        <>
          <Typography textAlign="center" variant="paragraph1">
            {t('warningPages.outdatedBrowser.description')}
          </Typography>
          <BrowserDetails>
            <BrowserDetailsItem>
              <Header>
                <CancelOutlinedIcon color="error" sx={{ width: '20px' }} />
                <Typography variant="caption1">
                  {t('warningPages.outdatedBrowser.yourVersion')}
                </Typography>
              </Header>
              <Typography variant="h3">
                {toLocale(clientBrowserVersion as string)}
              </Typography>
            </BrowserDetailsItem>
            <BrowserDetailsItem>
              <Header>
                <CheckCircleIcon color="success" sx={{ width: '20px' }} />
                <Typography variant="caption1">
                  {t('warningPages.outdatedBrowser.minVersion')}
                </Typography>
              </Header>
              <Typography variant="h3">
                {toLocale(recommendedBrowserVersion as string)}+
              </Typography>
            </BrowserDetailsItem>
          </BrowserDetails>
        </>
      }
      badge={
        <AlertCircleOutlinedIcon
          sx={{ color: 'neutral000', width: '40px', height: '40px' }}
        />
      }
    />
  )
}

const Header = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

const BrowserLogo = styled('img')`
  width: 24px;
  margin: 0 6px -6px 6px;
`

const BrowserDetailsItem = styled('div')`
  width: calc(50% - 6px);
  padding: 12px 8px;
  max-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.palette.neutral300};
  border-radius: 12px;
`
const BrowserDetails = styled('div')`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  width: 100%;
  justify-content: center;
  align-items: center;
`
